import type { FC } from 'react';
import { Icon } from '@iconify/react';
import { Avatar, Box, ButtonBase } from '@mui/material';
import { useNavigate } from 'react-router';
import { usePopover } from 'src/hooks/components/use-popover';
import { useAsyncCallback } from 'src/hooks/use-async-callback';
import { useAuthContext, useMe } from 'src/hooks/use-context';
import { useNotificationsContext } from 'src/hooks/use-context';
import { paths } from 'src/paths';
import { icons } from 'src/theme/icons';
import { AccountPopover } from './account-popover';

export const AccountButton: FC = () => {
  const navigate = useNavigate();

  const { signOut } = useAuthContext();
  const notifications = useNotificationsContext();
  const me = useMe();

  const popover = usePopover<HTMLButtonElement>();

  const [handleLogout] = useAsyncCallback(
    async (redirect?: string): Promise<void> => {
      popover.handleClose();

      await signOut('user-logout');
      // reset notifications after successful logout
      await notifications.handleLogout();
      // this should force login
      navigate(redirect ?? paths.auth.firebase.login);
    },
    [navigate, popover, signOut, notifications]
  );

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: 'divider',
          height: 40,
          width: 40,
          borderRadius: '50%'
        }}
        data-testid="account-button"
      >
        <Avatar
          sx={{
            height: 32,
            width: 32
          }}
        >
          <Icon icon={icons.user} />
        </Avatar>
      </Box>
      <AccountPopover
        anchorEl={popover.anchorRef.current}
        profile={me}
        onLogout={handleLogout}
        onClose={popover.handleClose}
        open={popover.open}
      />
    </>
  );
};
